import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Stack,
  Textarea,
  useToast
} from '@chakra-ui/react'
import * as Sentry from '@sentry/react'
import { useMutation } from '@tanstack/react-query'
import { RentalApplication } from '@homevest/types/rental-applications'
import { CreateRentalApplicationDecisionPayload } from '@homevest/types/rental-application-decisions'
import { RentalProgramType } from '@homevest/types/rentals'
import { getDefaultRentalProgramConfig } from '@homevest/utils/rental-program-configs'

import axios from 'lib/axios'
import { BaseFormField } from 'components/ChakraToolkit/FormField'
import { RentalAppQualificationResponse } from './useRentalAppQualification'

export const ApprovalModal = ({
  rentalApplication,
  qualificationResponse,
  isOpen,
  onClose
}: {
  rentalApplication: RentalApplication
  qualificationResponse: RentalAppQualificationResponse
  isOpen: boolean
  onClose: () => void
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ApproveRentalAppForm
          rentalApplication={rentalApplication}
          qualificationResponse={qualificationResponse}
          onClose={onClose}
        />
      </ModalContent>
    </Modal>
  )
}

const ApproveRentalAppForm = ({
  rentalApplication,
  qualificationResponse,
  onClose
}: {
  rentalApplication: RentalApplication
  qualificationResponse: RentalAppQualificationResponse
  onClose: () => void
}) => {
  const toast = useToast()
  const history = useHistory()
  const qualificationDetails = 'error' in qualificationResponse ? null : qualificationResponse
  const defaultProgramConfig = getDefaultRentalProgramConfig(
    rentalApplication.rental_program_type_applied_for as RentalProgramType
  )
  const standardDepositMultiple =
    qualificationDetails?.standard_deposit_rent_multiple ??
    defaultProgramConfig.standard_deposit_rent_multiple
  const additionalDepositMultiple =
    qualificationDetails?.additional_deposit_rent_multiple ??
    defaultProgramConfig.additional_deposit_rent_multiple

  const defaultDepositMultiple =
    qualificationDetails?.deposit_rent_multiple ?? standardDepositMultiple
  const [depositMultiple, setDepositMultiple] = useState(defaultDepositMultiple)
  const [notes, setNotes] = useState('')

  // Empty errors object for BaseFormField compatibility
  const errors = {} as Record<string, string>

  const { isLoading, mutate: approve } = useMutation({
    mutationFn: async () => {
      const payload: CreateRentalApplicationDecisionPayload = {
        rental_application_id: rentalApplication.id,
        is_approved: true,
        deposit_rent_multiple: depositMultiple,
        notes: notes
      }

      await axios.post(`/admin/rental_application_decisions`, payload)
    },
    onSuccess: () => {
      toast({
        title: 'Application Approved',
        description: 'The application was successfully approved!',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'bottom-right'
      })
      onClose()
      history.go(0) // refresh the page so that app status is updated
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: `Cannot approve application: ${error.message ?? ''}.`,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'bottom-right'
      })

      Sentry.captureException(error)
    }
  })

  useEffect(() => {
    setDepositMultiple(defaultDepositMultiple)
  }, [defaultDepositMultiple])

  return (
    <>
      <ModalHeader>Ready to approve this application?</ModalHeader>
      <ModalCloseButton />
      <ModalBody>
        <Stack spacing={4}>
          <BaseFormField
            field='deposit_rent_multiple'
            label='Deposit Rent Multiple'
            isRequired={false}
            errors={errors}
          >
            <DepositMultipleSelect
              value={depositMultiple}
              onChange={(e) => setDepositMultiple(Number(e.target.value))}
              standardDepositMultiple={standardDepositMultiple}
              additionalDepositMultiple={additionalDepositMultiple}
            />
          </BaseFormField>
          <BaseFormField field='notes' label='Notes' isRequired={false} errors={errors}>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
          </BaseFormField>
        </Stack>
      </ModalBody>
      <ModalFooter gap={2} flexDir='column'>
        <Button
          colorScheme='teal'
          isLoading={isLoading}
          isDisabled={isLoading}
          w='full'
          onClick={() => approve()}
        >
          Approve
        </Button>
      </ModalFooter>
    </>
  )
}

const DepositMultipleSelect = ({
  value,
  onChange,
  standardDepositMultiple,
  additionalDepositMultiple
}: {
  value: number
  onChange: React.ChangeEventHandler<HTMLSelectElement>
  standardDepositMultiple: number
  additionalDepositMultiple: number
}) => {
  return (
    <Select value={value} onChange={onChange}>
      <option value={standardDepositMultiple}>
        Standard Deposit: {standardDepositMultiple}x Rent
      </option>
      <option value={additionalDepositMultiple}>
        Additional Deposit: {additionalDepositMultiple}x Rent
      </option>
    </Select>
  )
}
